import React, { useState, useMemo, useCallback, FocusEvent, ChangeEvent, FormEvent } from 'react';

import { t } from '@sm/intl';

import { Typography } from '@wds/typography';
import { Checkbox } from '@wds/checkbox';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { FormGroup, FormField } from '@wds/form-group';
import { ProgressCircle } from '@wds/progress-circle';
import { errorHandler } from '@sm/webassets/dist/helpers/errorHandler';

import { EMPTY_STRING } from '~shared/util';

import useHubSpotFormAPI from '~pricing/hooks/useHubSpotAPI';
import COPY from '~pricing/pages/Enterprise/copy';

import { TranslationsPlaceHolderType } from '~pricing/types';
import { HS_ERROR_STATUS, parseHubspotApiResponse, saveFormData } from './helpers';
import {
  HubspotApiStatus,
  HubspotApiData,
  HubspotFormType,
  HubspotApiReturnStatus,
  FormState,
  HsFormFields,
} from './types';
import { validateAndGetFormData } from './Validation';

import HubSpotErrorBanner from './HubSpotErrorBanner';
import HubspotInput from './HubspotInput';
import useHubSpotStyles from './useStyles';

function RequestDemo({ children }: Readonly<TranslationsPlaceHolderType>): JSX.Element {
  return <strong>{children}</strong>;
}

function OptOutLink({ children }: Readonly<TranslationsPlaceHolderType>): JSX.Element {
  return (
    <a href="https://help.surveymonkey.com/en/surveymonkey/account/opt-out/#emails" rel="noreferrer" target="_blank">
      {children}
    </a>
  );
}

function PrivacyLink({ children }: Readonly<TranslationsPlaceHolderType>): JSX.Element {
  return (
    <a href="/mp/legal/privacy/" target="_blank">
      {children}
    </a>
  );
}

export default function HubSpotForm({ customOnSubmitFunction }: Readonly<HubspotFormType>): JSX.Element {
  const [formData, setFormData] = useState<FormState>({});
  const { status, data, error }: HubspotApiData = useHubSpotFormAPI<HubspotApiData>();
  const { CheckBoxWrapper } = useHubSpotStyles();

  const formFields = useMemo(() => data?.fields, [data?.fields]);

  const handleChange = useCallback(
    ({ target }: FocusEvent<HTMLInputElement>) => {
      const { name, value } = target;
      const validatedData = validateAndGetFormData(name, value);
      setFormData({
        ...formData,
        ...validatedData,
      });
    },
    [setFormData, formData]
  );

  const handleConsentChecked = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = target;
      setFormData({
        ...formData,
        [name]: {
          value: checked,
          color: undefined,
          errorMessage: checked ? undefined : t(COPY.ERROR),
        },
      } as FormState);
    },
    [setFormData, formData]
  );

  const validateFormFields = (fields: HsFormFields[], formDataState: FormState): FormState => {
    let newFormData = { ...formDataState };
    fields.forEach(({ name, hidden }) => {
      if (!hidden) {
        const currentValue = newFormData[name] ? newFormData[name].value : EMPTY_STRING;
        const submitErrors = validateAndGetFormData(name, currentValue);
        newFormData = { ...newFormData, ...submitErrors };
      }
    });
    return newFormData;
  };

  const handleFormSubmission = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!formFields || !formFields.length) {
        return;
      }
      const newFormData = validateFormFields(formFields, formData);
      setFormData(newFormData);
      saveFormData({ formFields, formData })
        .then((response: HubspotApiReturnStatus | undefined) => {
          if (response?.redirectUri) {
            window.location.assign(response?.redirectUri);
            if (customOnSubmitFunction) {
              customOnSubmitFunction();
            }
          } else {
            const errors = parseHubspotApiResponse(response as HubspotApiReturnStatus, formData);
            // if we got any errors, let's dissect them; field errors are also server errors
            if (errors) {
              const [fieldErrors, serverError] = errors;
              if (HS_ERROR_STATUS.includes(serverError?.errorType ?? 'error')) {
                errorHandler.logError(
                  new Error(`${serverError?.errorType} ${serverError?.message}`),
                  serverError.status, // message
                  serverError // metaData
                );
                // After reporting the error to RUM, should we do a Toast or something?
                // TODO: Add a toast or something
              }
              fieldErrors?.forEach(fs => setFormData({ ...formData, ...fs }));
            }
          }
        })
        .catch(err => {
          errorHandler.logError(err); // RUM reporting, threshold show flag this error
        });
    },
    [setFormData, formFields, formData, customOnSubmitFunction]
  );

  if (error) {
    return <HubSpotErrorBanner />;
  }

  return (
    <>
      {status === HubspotApiStatus.Success ? (
        <form autoComplete="off" onSubmit={handleFormSubmission} noValidate data-testid="HubSpotForm__Form">
          <FormGroup>
            <HubspotInput fields={formFields} formData={formData} handleOnBlur={handleChange} />
            <Box className={CheckBoxWrapper} p={3}>
              <FormField>
                <Checkbox
                  key="consent"
                  name="consent"
                  id="Hubspot__Consent"
                  data-testid="HubSpotForm__Consent"
                  defaultChecked={false}
                  onChange={handleConsentChecked}
                />
              </FormField>
              <Box display="flex" flexDirection="column" gap={2} justifyContent="flex-start">
                <Typography component="p" align="left" variant="bodySm">
                  {t(
                    { ...COPY.CONSENT_UPDATED },
                    {
                      optoutLink: OptOutLink,
                    }
                  )}
                </Typography>
                <Typography component="p" align="left" variant="bodySm">
                  {t(
                    { ...COPY.CONSENT_PRIVACY_NOTICE },
                    {
                      requestDemo: RequestDemo,
                      privacyNotice: PrivacyLink,
                    }
                  )}
                </Typography>
              </Box>
            </Box>
            <Box p={2}>
              <Button color="primary" buttonType="submit" size="md" variant="solid">
                {t(COPY.CONTACT_SALES)}
              </Button>
            </Box>
          </FormGroup>
        </form>
      ) : (
        <Box display="flex" flexJustify="center">
          <ProgressCircle valueText={EMPTY_STRING} size="lg" continuous />
        </Box>
      )}
    </>
  );
}
